import { zodResolver } from "@hookform/resolvers/zod";
import { TextField } from "@mui/material";
import { CaretDownIcon } from "@phosphor-icons/react";
import dayjs from "dayjs";
import { useState } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { useTranslations } from "use-intl";
import { object } from "zod";

import {
  HotelSearchTrackingEvents,
  rudderAnalytics,
  type SearchSource,
} from "@/app/lib/rudderstack";
import { SwipeableDrawer } from "@/components/Drawer/SwipeableDrawer";
import CheckInSelection from "@/features/hotels/components/hotels/CheckInSelection";
import {
  getDatesValidationSchema,
  getRoomsValidationSchema,
} from "@/features/hotels/components/hotels/validation";
import VisitorsSelection from "@/features/hotels/components/hotels/VisitorsSelections";
import type { THotelFormValues } from "@/features/hotels/types/types";

enum DrawerComponent {
  CheckInSelection = "CheckInSelection",
  VisitorsSelection = "VisitorsSelection",
}

type HotelSearchFormValues = Pick<THotelFormValues, "dates" | "rooms">;

interface Props {
  initialValues: HotelSearchFormValues;
  analyticsSource: SearchSource;
  onSubmit: (values: HotelSearchFormValues) => void;
}

export const HotelSearchForm = ({
  initialValues,
  analyticsSource,
  onSubmit,
}: Props) => {
  const t = useTranslations();
  const [drawerComponent, setDrawerComponent] =
    useState<DrawerComponent | null>(null);

  const methods = useForm<HotelSearchFormValues>({
    defaultValues: initialValues,
    resolver: zodResolver(
      object({
        dates: getDatesValidationSchema(t),
        rooms: getRoomsValidationSchema(t),
      }),
    ),
  });

  const { handleSubmit, setValue, control } = methods;

  const [rooms, dates] = useWatch({ control, name: ["rooms", "dates"] });

  const handleDrawerClose = () => {
    setDrawerComponent(null);

    handleSubmit((data) => {
      onSubmit(data);
    })();
  };

  const handleInputClick = (
    e: React.MouseEvent<HTMLDivElement>,
    component: DrawerComponent,
  ) => {
    e.preventDefault();
    setDrawerComponent(component);
  };

  const checkInValue = dayjs(dates.checkIn).format("D MMM");
  const checkOutValue = dates.checkOut
    ? dayjs(dates.checkOut).format("D MMM")
    : "_";

  return (
    <FormProvider {...methods}>
      <form className="flex gap-2">
        <TextField
          name="dates"
          onMouseDown={(e) => {
            handleInputClick(e, DrawerComponent.CheckInSelection);
          }}
          fullWidth
          label={t("hotels.search.checkInAndCheckOut")}
          value={`${checkInValue} - ${checkOutValue}`}
        />
        <TextField
          fullWidth
          name="visitors"
          label={t("hotels.search.visitors")}
          value={t("hotels.search.visitorsSumWithTypes", {
            adultsCount: rooms.reduce((acc, room) => acc + room.adults, 0),
            childrenCount: rooms.reduce((acc, room) => acc + room.children, 0),
          })}
          slotProps={{
            input: {
              endAdornment: (
                <CaretDownIcon
                  className="shrink-0"
                  color="black"
                  size={20}
                  weight="bold"
                />
              ),
            },
          }}
          sx={{
            input: {
              overflow: "hidden",
              textOverflow: "ellipsis",
            },
          }}
          onMouseDown={(e) => {
            handleInputClick(e, DrawerComponent.VisitorsSelection);
          }}
        />
      </form>

      <SwipeableDrawer
        onClose={() => setDrawerComponent(null)}
        open={!!drawerComponent}
        hasFullHeight={drawerComponent === DrawerComponent.CheckInSelection}
      >
        {drawerComponent === DrawerComponent.CheckInSelection ? (
          <CheckInSelection
            initialValues={{
              dates,
            }}
            onConfirm={(data) => {
              setValue("dates", data.dates, {
                shouldDirty: true,
              });
              handleDrawerClose();
            }}
          />
        ) : null}
        {drawerComponent === DrawerComponent.VisitorsSelection ? (
          <VisitorsSelection
            onConfirm={(data) => {
              rudderAnalytics.track(
                HotelSearchTrackingEvents.USER_CHANGED_HOTEL_TRAVELLERS,
                {
                  source: analyticsSource,
                  // for now we are not supporting multiple rooms booking so we send only first room data
                  travellers: data.rooms[0],
                },
              );
              handleDrawerClose();
            }}
          />
        ) : null}
      </SwipeableDrawer>
    </FormProvider>
  );
};
